import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { RefreshCw, Loader2, Clock, Search, ArrowLeft, ExternalLink, User } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function RelancesPage() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterCommercial, setFilterCommercial] = useState(null);
    const [allCommerciaux, setAllCommerciaux] = useState([]);

    useEffect(() => {
        axios.get(`${API}/api/devis/relances`, { withCredentials: true })
            .then(({ data }) => setItems(data))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
        axios.get(`${API}/api/inventaires/config`, { withCredentials: true })
            .then(({ data }) => setAllCommerciaux(data.commerciaux || []))
            .catch(() => {});
    }, []);

    // Build commercial stats: all commercials even if 0. On compte les dossiers
    // uniques (= clients) et non les devis individuels pour rester coherent avec
    // l'affichage groupe ci-dessous.
    const commercialList = (() => {
        const stats = {};
        allCommerciaux.forEach(c => {
            stats[c.id] = { id: c.id, name: c.name, couleur: c.couleur || '#94a3b8', count: 0, _seen: new Set() };
        });
        items.forEach(item => {
            const key = item.commercial_id || '_none';
            if (!stats[key]) {
                stats[key] = { id: item.commercial_id, name: item.commercial_name || 'Non assigné', couleur: item.commercial_couleur || '#94a3b8', count: 0, _seen: new Set() };
            }
            const dossierKey = item.inventaire_id || `devis_${item.devis_id}`;
            if (!stats[key]._seen.has(dossierKey)) {
                stats[key]._seen.add(dossierKey);
                stats[key].count++;
            }
        });
        return Object.values(stats).sort((a, b) => b.count - a.count);
    })();

    const filtered = items.filter(item => {
        const matchSearch = !search.trim() || (item.client_nom || '').toLowerCase().includes(search.toLowerCase());
        const matchCommercial = !filterCommercial || (item.commercial_id || '_none') === filterCommercial;
        return matchSearch && matchCommercial;
    });

    // Regroupement par dossier (inventaire_id) : 1 ligne par client comme dans la
    // liste devis. Le bouton mene vers l'Apercu devis du client (DevisDossierPage).
    const ACTION_PRIORITY = { relance_3: 3, relance_2: 2, relance_1: 1 };
    const groupedMap = new Map();
    filtered.forEach(item => {
        const key = item.inventaire_id || `devis_${item.devis_id}`;
        const prev = groupedMap.get(key);
        if (!prev) {
            groupedMap.set(key, { ...item, _count: 1 });
        } else {
            // On garde l'action la plus prioritaire (relance_3 > relance_2 > relance_1)
            if ((ACTION_PRIORITY[item.action] || 0) > (ACTION_PRIORITY[prev.action] || 0)) {
                prev.action = item.action;
                prev.remarque_relance = item.remarque_relance;
                prev.elapsed = item.elapsed;
                prev.devis_id = item.devis_id;
            }
            prev._count += 1;
        }
    });
    const grouped = Array.from(groupedMap.values());

    return (
        <div className="p-4 md:p-6 lg:p-8" data-testid="relances-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm" onClick={() => navigate('/devis')} data-testid="relances-back-btn">
                        <ArrowLeft size={16} className="mr-1.5" />
                        Retour
                    </Button>
                    <div>
                        <h1
                            className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                            data-testid="relances-title"
                        >
                            Relances à effectuer
                        </h1>
                        <p className="mt-1 text-slate-500" data-testid="relances-count">
                            {(() => {
                                const uniqueDossiers = new Set(items.map(i => i.inventaire_id || `devis_${i.devis_id}`)).size;
                                return `${uniqueDossiers} client${uniqueDossiers > 1 ? 's' : ''} à relancer`;
                            })()}
                        </p>
                    </div>
                </div>
            </div>

            {/* Commercial cards */}
            {commercialList.length > 0 && (
                <div className="grid gap-3 mb-6" style={{ gridTemplateColumns: `repeat(${commercialList.length}, 1fr)` }} data-testid="relances-commercial-stats">
                    {commercialList.map(c => (
                        <Card
                            key={c.id || '_none'}
                            className={`rounded-lg border shadow-sm cursor-pointer transition-all hover:shadow-md ${
                                filterCommercial === (c.id || '_none')
                                    ? 'ring-2 ring-offset-1 bg-slate-50'
                                    : 'bg-white border-slate-200'
                            }`}
                            style={filterCommercial === (c.id || '_none') ? { borderColor: c.couleur } : {}}
                            onClick={() => setFilterCommercial(prev => prev === (c.id || '_none') ? null : (c.id || '_none'))}
                            data-testid={`relances-commercial-card-${c.id || 'none'}`}
                        >
                            <CardContent className="px-4 py-3 flex items-center gap-2.5">
                                <div
                                    className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                                    style={{ backgroundColor: c.couleur + '20' }}
                                >
                                    <span className="text-sm font-bold" style={{ color: c.couleur }}>{c.count}</span>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-slate-800 truncate">{c.name}</p>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Search */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
                <CardContent className="p-4">
                    <div className="relative flex-1">
                        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            placeholder="Rechercher par nom..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10"
                            data-testid="relances-search"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                        <RefreshCw size={20} className="text-orange-500" />
                        Relances ({grouped.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" />
                        </div>
                    ) : grouped.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                            Aucune relance à effectuer
                        </div>
                    ) : (
                        <div className="overflow-x-auto -mx-0">
                            <Table data-testid="relances-table" className="min-w-[600px]">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Client</TableHead>
                                        <TableHead>Commercial</TableHead>
                                        <TableHead>Action</TableHead>
                                        <TableHead>Depuis</TableHead>
                                        <TableHead>Remarque</TableHead>
                                        <TableHead className="text-right"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {grouped.map(item => (
                                        <TableRow key={item.inventaire_id || item.devis_id} className={item.action === 'relance_1' ? 'bg-amber-50/30' : item.action === 'relance_2' ? 'bg-rose-50/30' : 'bg-violet-50/30'} data-testid={`relance-row-${item.inventaire_id || item.devis_id}`}>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                                                        item.action === 'relance_1' ? 'bg-amber-100' : item.action === 'relance_2' ? 'bg-rose-100' : 'bg-violet-100'
                                                    }`}>
                                                        <RefreshCw size={16} className={
                                                            item.action === 'relance_1' ? 'text-amber-600' : item.action === 'relance_2' ? 'text-rose-600' : 'text-violet-600'
                                                        } />
                                                    </div>
                                                    <p className="font-medium text-slate-900">{item.client_nom || '-'}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {item.commercial_name ? (
                                                    <span
                                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white"
                                                        style={{ backgroundColor: item.commercial_couleur || '#94a3b8' }}
                                                    >
                                                        {item.commercial_name}
                                                    </span>
                                                ) : (
                                                    <span className="text-sm text-slate-400 italic">Non assigné</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {item.action === 'relance_1' ? (
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                                                        A relancer la 1ère fois
                                                    </span>
                                                ) : item.action === 'relance_2' ? (
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-rose-100 text-rose-700 border border-rose-200">
                                                        A relancer la 2ème fois
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-violet-100 text-violet-700 border border-violet-200">
                                                        A relancer la 3ème fois
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1.5">
                                                    <Clock size={14} className="text-amber-500" />
                                                    <span className="text-sm font-medium text-amber-700">{item.elapsed}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-sm text-slate-600">{item.remarque_relance || '-'}</span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    size="sm"
                                                    className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white"
                                                    onClick={() => navigate(`/devis/${item.inventaire_id}`)}
                                                    data-testid={`relance-go-${item.devis_id}`}
                                                >
                                                    <ExternalLink size={14} className="mr-1.5" />
                                                    Voir devis
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
